import React from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, RotateCw } from "lucide-react";
import { sharedApi } from "@/lib/api";
import StoreScopeTag from "@/components/partner/StoreScopeTag";

/**
 * Iter-623 — "📊 Today at a glance" live status panel for the store owner.
 *   GET /vendors/me/status-panel (owner JWT, X-Store-Id via sharedApi)
 * NZ-calendar-day tiles + last 7 days + payout note; auto-refreshes
 * every 30 s. KYC-approved vendor owners only; rendered ABOVE the
 * sound-box card on the Partner Program page.
 */
const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

const nzd = (v) => `$${Number(v || 0).toFixed(2)}`;

function Tile({ big, small, tiny, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex-1 min-w-[130px] rounded-xl bg-slate-50 border border-slate-200 px-3.5 py-3"
    >
      <div className="font-display text-xl font-black text-slate-900">{big}</div>
      <div className="text-xs font-bold text-slate-600 mt-0.5">{small}</div>
      {tiny ? <div className="text-[11px] text-slate-400 mt-0.5">{tiny}</div> : null}
    </div>
  );
}

export default function VendorStatusPanel() {
  const meQ = useQuery({
    queryKey: ["vendor-me"],
    retry: false,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me", OPTS)).data,
  });

  const panelQ = useQuery({
    queryKey: ["vendor-status-panel"],
    enabled: meQ.data?.kyc_status === "approved",
    refetchInterval: 30_000, // live-ish: refresh every 30 s
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me/status-panel", OPTS)).data,
  });

  // KYC-approved vendor owners only. A 404 means the backend build on
  // this host doesn't have the panel yet — stay hidden, no error card.
  if (!meQ.data || meQ.data.kyc_status !== "approved") return null;
  if (panelQ.isError && panelQ.error?.response?.status === 404) return null;

  const d = panelQ.data;
  return (
    <section data-testid="vendor-status-panel" className="mt-6 rounded-2xl bg-white shadow-card p-6 md:p-8">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="w-9 h-9 rounded-xl bg-sky-100 text-sky-600 grid place-items-center shrink-0">
          <BarChart3 className="w-4 h-4" />
        </span>
        <h2 className="font-display text-2xl font-black text-slate-900">📊 Today at a glance</h2>
        <StoreScopeTag />
        <button
          type="button"
          data-testid="vsp-refresh"
          onClick={() => panelQ.refetch()}
          className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-[color:var(--brand-blue)] hover:underline min-h-[32px]"
        >
          <RotateCw className={`w-3.5 h-3.5 ${panelQ.isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {panelQ.isError && (
        <p className="mt-3 text-sm text-rose-600" data-testid="vsp-error">
          Could not load status — try Refresh.
        </p>
      )}
      {!d && !panelQ.isError && <p className="mt-3 text-sm text-slate-500">Loading…</p>}
      {d && (
        <>
          <div className="mt-4 flex flex-wrap gap-2.5">
            <Tile
              big={d.visits_today}
              small="Visits today"
              tiny={d.live_now ? `${d.live_now} live now` : undefined}
              testId="vsp-visits"
            />
            <Tile big={d.today.count} small="Paid sales" tiny={`${nzd(d.today.charged_nzd)} charged`} testId="vsp-paid" />
            <Tile big={nzd(d.today.vendor_take_nzd)} small="Your takings" tiny="after fees & discounts" testId="vsp-take" />
            <Tile
              big={d.today.points_redeemed}
              small="Points redeemed"
              tiny={`${nzd(d.today.points_value_nzd)} value`}
              testId="vsp-points"
            />
          </div>
          <div className="mt-2.5 flex flex-wrap gap-2.5">
            <Tile
              big={d.pending_reviews}
              small="Reviews to approve"
              tiny={d.review_windows_open ? `${d.review_windows_open} in 48h window` : "all caught up"}
              testId="vsp-reviews"
            />
            <Tile
              big={nzd(d.last_7_days.vendor_take_nzd)}
              small="Last 7 days"
              tiny={`${d.last_7_days.count} sales`}
              testId="vsp-week"
            />
            {d.waiting_for_scan ? (
              <Tile
                big={d.waiting_for_scan}
                small="Waiting for scan"
                tiny="till sales not yet claimed"
                testId="vsp-waiting"
              />
            ) : null}
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="vsp-payout-note">
            💸 Payouts: {d.payouts?.note}
            {Number(d.payouts?.deferred_payables_owed_nzd) > 0
              ? ` Deferred discount payables still owed: ${nzd(d.payouts.deferred_payables_owed_nzd)} (recovered from upcoming sales).`
              : ""}{" "}
            Points redemptions are reimbursed less a 5% settlement fee.
          </p>
          {/* Iter-636 — backend-authored fee explanation (vendor pays the
              $0.20 only on a real review); static fallback when the shared
              backend on this host doesn't send fee_note yet. */}
          <p className="mt-1.5 text-xs text-slate-500" data-testid="vsp-fee-note">
            🏷️ Fees:{" "}
            {d.fee_note ||
              "You pay the $0.20 AllSale fee only when a customer posts a review — skipped visits cost you nothing."}
          </p>
          {d.pending_reviews > 0 && (
            <button
              type="button"
              data-testid="vsp-go-reviews"
              onClick={() =>
                document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
              className="mt-3 inline-flex items-center rounded-full border border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] text-sm font-bold px-5 py-2.5 hover:bg-blue-50 transition-colors"
            >
              Approve {d.pending_reviews} waiting review{d.pending_reviews === 1 ? "" : "s"} →
            </button>
          )}
        </>
      )}
    </section>
  );
}
